import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import Corners from "../wam/Corners";
import { REPLAY_CONFIG } from "@/config/replay";
import { appendAttributionToUrl } from "@/lib/utm";

const GHL_SCRIPT = "https://link.smallbusinessautomator.com/js/form_embed.js";

export default function ReplayBooking() {
  const [ready, setReady] = useState(false);
  const iframeRef = useRef(null);
  const cfg = REPLAY_CONFIG;

  const src = useMemo(
    () => appendAttributionToUrl(cfg.GHL_CALENDAR_EMBED_URL || ""),
    [cfg.GHL_CALENDAR_EMBED_URL]
  );

  useEffect(() => {
    if (!cfg.GHL_CALENDAR_EMBED_URL) return;
    if (document.querySelector(`script[src="${GHL_SCRIPT}"]`)) return;
    const s = document.createElement("script");
    s.src = GHL_SCRIPT;
    s.async = true;
    document.body.appendChild(s);
  }, [cfg.GHL_CALENDAR_EMBED_URL]);

  return (
    <section
      id="book"
      className="relative overflow-hidden py-28 md:py-40 border-t wam-hairline"
      data-testid="replay-booking"
    >
      <div
        aria-hidden
        className="absolute left-1/2 top-[8%] -translate-x-1/2 h-[720px] w-[900px] pointer-events-none opacity-[0.35]"
        style={{
          background:
            "radial-gradient(closest-side, rgba(201,168,76,0.2), transparent 70%)",
        }}
      />
      <div className="relative mx-auto max-w-[1200px] px-6 md:px-10">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9 }}
          className="font-display text-center mx-auto max-w-[1100px]"
          style={{ fontSize: "clamp(40px, 6.4vw, 96px)", lineHeight: 0.94, letterSpacing: "-0.035em" }}
        >
          Stop letting good leads
          <br />
          <span className="text-[#C9A84C]">go cold.</span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.65 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.3 }}
          className="mx-auto mt-8 max-w-[620px] text-center font-body text-[15px] leading-[1.75] text-[#F0ECE4]"
        >
          You watched the system. Now let&rsquo;s look at the one change that
          can make it more useful for your business.
        </motion.p>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 font-mono text-[10px] tracking-[0.28em] text-[#F0ECE4]/50">
          <span>FREE STRATEGY CALL</span>
          <span className="text-[#C9A84C]/40">·</span>
          <span>BUILT AROUND YOUR REAL CONTEXT</span>
          {cfg.REAL_CAPACITY && (
            <>
              <span className="text-[#C9A84C]/40">·</span>
              <span className="text-[#C9A84C]">{cfg.REAL_CAPACITY.toUpperCase()}</span>
            </>
          )}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="relative mt-14 mx-auto max-w-[1080px]"
        >
          <div className="relative border border-[#C9A84C]/25 bg-[#0b0b0e] p-3 md:p-4 shadow-[0_60px_180px_-40px_rgba(0,0,0,0.9)]">
            <Corners />
            <div className="relative bg-white">
              {!ready && cfg.GHL_CALENDAR_EMBED_URL && (
                <div className="absolute inset-0 flex items-center justify-center bg-[#0b0b0e] z-10">
                  <div className="font-mono text-[11px] tracking-[0.3em] text-[#C9A84C]/70 animate-pulse">
                    LOADING CALENDAR&hellip;
                  </div>
                </div>
              )}
              {cfg.GHL_CALENDAR_EMBED_URL ? (
                <iframe
                  ref={iframeRef}
                  title="Book your SBA strategy call"
                  src={src}
                  style={{
                    width: "100%",
                    border: "none",
                    overflow: "hidden",
                    display: "block",
                    minHeight: "760px",
                  }}
                  scrolling="no"
                  onLoad={() => setReady(true)}
                  data-testid="replay-calendar-iframe"
                />
              ) : (
                <div className="flex items-center justify-center bg-[#0b0b0e] p-16 text-center">
                  <div>
                    <div className="font-mono text-[10px] tracking-[0.3em] text-[#C9A84C]">
                      CALENDAR NOT YET CONFIGURED
                    </div>
                    <div className="mt-3 font-body text-[13px] text-[#F0ECE4]/50 max-w-[420px]">
                      {/* TODO: Set GHL_CALENDAR_EMBED_URL in /app/frontend/src/config/replay.js */}
                      Add the SBA strategy-call calendar embed URL to render this
                      booking flow.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {cfg.GHL_CALENDAR_EMBED_URL && (
            <div className="mt-4 text-center">
              <a
                href={src}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-[10px] tracking-[0.28em] text-[#F0ECE4]/40 hover:text-[#C9A84C] transition-colors"
                data-testid="replay-fallback-calendar-link"
              >
                Calendar not loading? Open in new tab →
              </a>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
